import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, doc, setDoc, increment } from 'firebase/firestore';
import { getAuth, signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { getAnalytics, logEvent } from "firebase/analytics";


// --- FIREBASE CONFIG ---
const firebaseConfig = {
  apiKey: "AIzaSyDCS8eqA2_mVi2k7KLHPhAMZYM_4xfxc0w",
  authDomain: "crrt-1b16b.firebaseapp.com",
  projectId: "crrt-1b16b",
  storageBucket: "crrt-1b16b.appspot.com",
  messagingSenderId: "292115390196",
  appId: "1:292115390196:web:3f59beac63281baa3cc8a1",
  measurementId: "G-3ZV3VN1H9B"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const analytics = getAnalytics(app);

// --- DATA ---
// Centralized data for all pathologies and findings
const interactionData = {
    normal: { 
        key: 'normal', 
        type: 'disease', 
        name: 'Normal State', 
        description: 'In a healthy glomerulus, the filtration barrier is intact...', 
        implications: ['No significant proteinuria or hematuria.', 'Normal Glomerular Filtration Rate (GFR).'], 
        corpuscleChanges: '• Normal architecture', 
        barrierChanges: '• Intact filtration barrier', 
        functionalState: ['normal'],
        corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/normalglom-light.jpg?alt=media&token=309ae91d-8dd3-40d2-97ad-66aa68deb1fc'],
        emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/normalglom-em.png?alt=media&token=a9cc0425-aada-4666-bd22-ca87aa87bbd8']
    },
    podocyte_effacement: { key: 'podocyte_effacement', type: 'finding', name: 'Podocyte Effacement', description: 'The delicate foot processes of the podocytes retract, flatten, and fuse...', implications: ['Primary cause of heavy proteinuria (nephrotic syndrome).', 'Key feature of Minimal Change Disease and FSGS.'], animation: ['podocyte_effacement', 'proteinuria'], proteinuriaLevel: 'massive', corpuscleChanges: '• (Not visible by light microscopy)', barrierChanges: '• Diffuse foot process effacement', functionalState: ['leaky'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/podocyteefface-em1.png?alt=media&token=5389e594-d242-4a2d-9e6d-6c824bbbe4ae', 'https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/podocyteefface-em2.png?alt=media&token=837644e0-a0b8-430f-af4b-a8ea6814d4f1'] },
    subepithelial_deposits: { key: 'subepithelial_deposits', type: 'finding', name: 'Subepithelial Deposits', description: 'Immune complexes (antigen-antibody pairs) deposit on the outer side of the GBM...', implications: ['Characteristic of Membranous Nephropathy.', 'Triggers GBM thickening and significant proteinuria.'], animation: ['subepithelial_deposits', 'proteinuria'], proteinuriaLevel: 'massive', corpuscleChanges: '• Thickened basement membranes', barrierChanges: '• Subepithelial deposits & GBM "spikes"', functionalState: ['leaky'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/subepideposits-em1.png?alt=media&token=25a824e5-4627-4eb6-8f4a-d414b0e07938', 'https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/subepideposits-em2.png?alt=media&token=9d57837f-34a7-48f6-a5aa-bd6b5bf6fe02'] },
    subendothelial_deposits: { key: 'subendothelial_deposits', type: 'finding', name: 'Subendothelial Deposits', description: 'Immune complexes are located between the capillary endothelium and the GBM...', implications: ['Hallmark of Class III/IV Lupus Nephritis and MPGN.', 'Often leads to endothelial injury and cell proliferation.'], animation: ['subendothelial_deposits', 'hematuria'], corpuscleChanges: '• Immune deposits visible', barrierChanges: '• Subendothelial deposits', functionalState: ['inflamed'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/subendodeposits-em.jpg?alt=media&token=d026bba6-c114-46c3-8059-9df9f42cba13'] },
    mesangial_hypercellularity: { key: 'mesangial_hypercellularity', type: 'finding', name: 'Mesangial Hypercellularity', description: 'An increase in the number of mesangial cells and the amount of mesangial matrix...', implications: ['Often associated with hematuria and proteinuria.', 'Seen in IgA Nephropathy, Lupus Nephritis, and MPGN.'], animation: 'mesangial_hypercellularity', corpuscleChanges: '• Mesangial proliferation & matrix expansion', barrierChanges: '• (No direct change)', functionalState: ['inflamed'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/mesangialhyper-light1.jpg?alt=media&token=a1d4e9ac-ae7f-4e76-9fe1-4eac6fee434a', 'https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/mesangialhyper-light2.jpg?alt=media&token=8d824c95-9881-4e27-8c0b-b26a342c0dcc'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/mesangialhyper-em1.png?alt=media&token=4aebacdc-0fd1-4430-a782-f8671a77f3cf'] },
    endocapillary_hypercellularity: { key: 'endocapillary_hypercellularity', type: 'finding', name: 'Endocapillary Hypercellularity', description: 'An influx of inflammatory cells (neutrophils, monocytes) and swelling of endothelial cells within the capillary lumens...', implications: ['A feature of proliferative glomerulonephritis (e.g., post-infectious).', 'Reduces blood flow and GFR, causes hematuria.'], animation: ['endocapillary_hypercellularity', 'hematuria'], corpuscleChanges: '• Occluded, hypercellular capillary lumens', barrierChanges: '• Swollen endothelium, inflammatory cells', functionalState: ['inflamed'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/endocap-light1.jpg?alt=media&token=ce1ff9e7-9a3d-4d5e-b9e5-0353c45eb9ab', 'https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/endocap-light2.jpg?alt=media&token=80d52b69-d21b-453d-b51d-5982cf6b28cc'] },
    cellular_crescent: { key: 'cellular_crescent', type: 'finding', name: 'Cellular Crescent', description: 'A severe form of injury where parietal epithelial cells (lining Bowman\'s capsule) and inflammatory cells proliferate in Bowman\'s space...', implications: ['Indicates rapidly progressive glomerulonephritis (RPGN).', 'Leads to rapid, severe loss of kidney function.'], animation: ['cellular_crescent', 'hematuria', 'proteinuria'], proteinuriaLevel: 'moderate', corpuscleChanges: '• Parietal cell crescent formation<br>• Glomerular tuft compression', barrierChanges: '• Severe injury pattern', functionalState: ['inflamed', 'destroyed', 'leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/crescent-light1.jpg?alt=media&token=fd7da356-217e-4914-9d5c-eeebe903c2c2', 'https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/crescent-light2.jpg?alt=media&token=8c88b93f-ea27-4f9b-a536-e947bdf525f5'] },
    tram_tracking: { key: 'tram_tracking', type: 'finding', name: 'GBM Duplication ("Tram Tracks")', description: 'The GBM appears split or duplicated. This is caused by the formation of a new layer of basement membrane in response to chronic injury, often from subendothelial deposits, as mesangial cells interpose themselves.', implications: ['Classic finding in Membranoproliferative Glomerulonephritis (MPGN).', 'Associated with significant proteinuria and hematuria.'], animation: ['tram_tracking', 'hematuria', 'proteinuria'], proteinuriaLevel: 'moderate', corpuscleChanges: '• Thickened, duplicated GBM', barrierChanges: '• Thickened GBM', functionalState: ['inflamed', 'leaky'], corpuscleImageUrl: ['https://storage.googleapis.com/crrt-1b16b.firebasestorage.app/gbm-duplication-light.png'] },    thinning_gbm: { key: 'thinning_gbm', type: 'finding', name: 'Thinning of the GBM', description: 'The glomerular basement membrane is abnormally thin...', implications: ['Often leads to persistent microscopic hematuria.', 'Can be benign or progressive depending on the cause.'], animation: ['thinning_gbm', 'hematuria'], corpuscleChanges: '• Abnormally thin basement membranes', barrierChanges: '• Significant thinning of the GBM', functionalState: ['fragile'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/thin-gbm-em1.png?alt=media&token=e2c72602-95f1-4317-a072-c41fa9f7f6f5'] },
    minimal_change: { key: 'minimal_change', type: 'disease', name: 'Minimal Change Disease', description: 'Characterized by diffuse effacement of podocyte foot processes...', implications: ['Most common cause of nephrotic syndrome in children.', 'Sudden onset of edema, heavy proteinuria.', 'Typically responds well to corticosteroids.'], animation: ['podocyte_effacement', 'proteinuria'], proteinuriaLevel: 'massive', corpuscleChanges: '• Normal by light microscopy', barrierChanges: '• Diffuse foot process effacement', functionalState: ['leaky'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/minimalchange-em1.png?alt=media&token=fff8c5c7-5201-4d23-bed6-fc82023954a2'] },
    membranous: { key: 'membranous', type: 'disease', name: 'Membranous Nephropathy', description: 'Caused by subepithelial immune deposits...', implications: ['Leading cause of nephrotic syndrome in non-diabetic adults.', 'Thickened GBM with "spikes" on biopsy.', 'Associated with anti-PLA2R antibodies.'], animation: ['subepithelial_deposits', 'proteinuria'], proteinuriaLevel: 'massive', corpuscleChanges: '• Uniformly thickened basement membranes', barrierChanges: '• Subepithelial deposits & GBM "spikes"', functionalState: ['leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/membranous-light1.jpg?alt=media&token=13a9b0b7-3214-4c9e-889d-800332e84217', 'https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/membranous-light2.jpg?alt=media&token=f895b097-b380-4292-bb46-9bbdae08b04d'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/membranous-em1.png?alt=media&token=87d5dced-38aa-4e76-b646-f0ff016ef0b9'] },
    fsgs: { key: 'fsgs', type: 'disease', name: 'Focal Segmental Glomerulosclerosis', description: 'A pattern of scarring (sclerosis) that affects some glomeruli (focal) and only parts of those glomeruli (segmental)...', implications: ['Common cause of nephrotic syndrome in children and adults.', 'Can be primary or secondary to other conditions.', 'Often progresses to kidney failure.'], animation: ['fsgs', 'proteinuria'], proteinuriaLevel: 'massive', corpuscleChanges: '• Segmental sclerosis & hyalinosis<br>• Adhesion to Bowman\'s capsule (synechia)', barrierChanges: '• Segmental podocyte effacement & loss', functionalState: ['normal', 'scarred', 'leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/fsgs-light1.jpg?alt=media&token=c0afbe0c-420b-460d-9dc6-b88bda866c47', 'https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/fsgs-light2.jpg?alt=media&token=23def6c0-6ae9-4f78-a0fb-a94dbde5122a'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/fsgs-em1.png?alt=media&token=125faad8-f5d6-4adc-88d7-68acf0f89e4e'] },
    iga: { key: 'iga', type: 'disease', name: 'IgA Nephropathy', description: 'The most common glomerulonephritis worldwide, caused by deposition of IgA-containing immune complexes in the mesangium...', implications: ['Episodic macroscopic hematuria (often with URI).', 'Persistent microscopic hematuria.', 'Variable proteinuria and progression.'], animation: ['mesangial_hypercellularity', 'iga_deposits', 'hematuria', 'proteinuria'], proteinuriaLevel: 'moderate', corpuscleChanges: '• Mesangial hypercellularity<br>• Mesangial IgA deposits', barrierChanges: '• Minimal changes on EM', functionalState: ['inflamed', 'leaky'] },
    ic_mpgn: { key: 'ic_mpgn', type: 'disease', name: 'Immune-Complex MPGN', description: 'A pattern of injury characterized by mesangial and endocapillary hypercellularity and GBM duplication ("tram-tracking")...', implications: ['Mixed nephritic-nephrotic presentation.', 'Low C3 and C4 levels are common.', '"Tram-track" GBM is the classic biopsy finding.'], animation: ['tram_tracking', 'endocapillary_hypercellularity', 'mesangial_hypercellularity', 'ic_deposits', 'subendothelial_deposits', 'hematuria', 'proteinuria'], proteinuriaLevel: 'moderate', corpuscleChanges: '• Lobular, hypercellular tuft<br>• "Tram-tracking" of GBM', barrierChanges: '• Subendothelial and/or mesangial deposits<br>• Thickened GBM', functionalState: ['inflamed', 'leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/mpgn-light1.png?alt=media&token=5ad3a1c4-49f6-4a44-89d3-22f8d4dc1493'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/mpgn-em1.png?alt=media&token=381db74a-94a1-4dde-9ab1-810d82c8daf0'] },
    post_infectious_gn: { key: 'post_infectious_gn', type: 'disease', name: 'Post-Infectious GN', description: 'A classic nephritic syndrome that occurs after an infection...', implications: ['Presents 1-3 weeks after pharyngitis or 3-6 weeks after impetigo.', 'Cola-colored urine (hematuria), edema, hypertension.', 'Low serum C3 is a key finding.', 'Biopsy shows large, subepithelial "humps" on EM.'], animation: ['post_infectious_humps', 'endocapillary_hypercellularity', 'mesangial_hypercellularity', 'hematuria', 'proteinuria', 'starry_sky_c3'], proteinuriaLevel: 'moderate', corpuscleChanges: '• Diffusely hypercellular, exudative glomeruli<br>• Granular "starry sky" C3 deposits', barrierChanges: '• Large, subepithelial "humps"<br>• Inflammatory cell presence', functionalState: ['inflamed', 'leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/pign-light1.jpg?alt=media&token=d564bd02-40ae-4fc6-9e67-c3706fada113'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/pign-em1.png?alt=media&token=a9254798-095a-452a-968c-8648187815db'] },
    c3_gn: { key: 'c3_gn', type: 'disease', name: 'C3 Glomerulonephritis', description: 'Caused by dysregulation of the alternative complement pathway...', implications: ['Low serum C3, normal C4.', 'Variable presentation from mild hematuria to RPGN.', 'Can recur in transplanted kidneys.'], animation: ['mesangial_hypercellularity', 'endocapillary_hypercellularity', 'c3_deposits', 'subendothelial_deposits', 'tram_tracking', 'hematuria', 'proteinuria'], proteinuriaLevel: 'moderate', corpuscleChanges: '• Lobular, hypercellular tuft<br>• "Tram-tracking" of GBM<br>• C3 deposits in mesangium/capillaries', barrierChanges: '• Subendothelial & mesangial deposits<br>• Thickened GBM', functionalState: ['inflamed', 'leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/c3gn-light1.jpg?alt=media&token=30317b41-c1a2-49ff-9706-11ded5401d1f'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/c3gn-em1.png?alt=media&token=77fb7aef-e2da-40b0-9781-386efbf25ee9'] },
    ddd: { key: 'ddd', type: 'disease', name: 'Dense Deposit Disease', description: 'A severe form of C3 Glomerulopathy where the alternative complement pathway dysregulation leads to the formation of highly dense, ribbon-like deposits within the GBM itself.', implications: ['Low serum C3.', 'Presents in children and adults.', 'High rate of progression to kidney failure.'], animation: ['mesangial_hypercellularity', 'c3_deposits', 'ddd_band', 'tram_tracking', 'hematuria', 'proteinuria'], proteinuriaLevel: 'moderate', corpuscleChanges: '• Lobular, hypercellular tuft<br>• "Tram-tracking" of GBM<br>• Mesangial C3 deposits', barrierChanges: '• Intramembranous "ribbon" deposits<br>• Thickened GBM', functionalState: ['inflamed', 'leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ddd-light1.jpg?alt=media&token=c11d9677-5202-4697-aed0-0ff6513b9b54'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ddd-em1.jpg?alt=media&token=e9285c26-738b-4ca2-8f34-9b8ad21d2035', 'https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ddd-em2.png?alt=media&token=9e128078-06ac-4590-ac98-9a688445a7f5'] },
    tma: { key: 'tma', type: 'disease', name: 'Thrombotic Microangiopathy', description: 'A pattern of severe endothelial injury leading to the formation of microthrombi in capillaries and arterioles...', implications: ['Presents with acute kidney injury, thrombocytopenia, and microopathic hemolytic anemia.', 'Causes include TTP, HUS, drugs, and autoimmune disease.'], animation: ['tma_thrombi', 'endocapillary_hypercellularity', 'hematuria'], corpuscleChanges: '• Fibrin thrombi in capillaries', barrierChanges: '• Endothelial swelling & injury', functionalState: ['clogged'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/tma-light1.jpg?alt=media&token=ec11ef94-d756-429a-be89-9fc02a254a19', 'https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/tma-light2.jpg?alt=media&token=8104a0ea-ac3e-42db-9cb8-c22abb8bc7f4'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/tma-em1.png?alt=media&token=67d95e65-cbc7-4359-8970-8f3ba14bb939'] },
    alport: { key: 'alport', type: 'disease', name: 'Alport Syndrome', description: 'A genetic disorder caused by mutations in the genes for type IV collagen...', implications: ['Progressive kidney failure.', 'Sensorineural hearing loss.', 'Ocular abnormalities (e.g., lenticonus).'], animation: ['thinning_gbm', 'hematuria'], corpuscleChanges: '• Thin basement membranes', barrierChanges: '• Thinning and splitting of the GBM', functionalState: ['fragile'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/alport-em1.png?alt=media&token=3e2449e7-2a0d-422a-bf63-825718a586da'] },
    anti_gbm: { key: 'anti_gbm', type: 'disease', name: 'Anti-GBM Nephritis', description: 'An autoimmune disease where antibodies attack a component of the GBM...', implications: ['Rapidly progressive glomerulonephritis (RPGN).', 'Often associated with pulmonary hemorrhage (Goodpasture\'s syndrome).', 'Requires urgent plasma exchange and immunosuppression.'], animation: ['cellular_crescent', 'linear_deposits', 'hematuria', 'proteinuria', 'barrier_destruction'], proteinuriaLevel: 'moderate', corpuscleChanges: '• Diffuse crescent formation<br>• Linear IgG deposits', barrierChanges: '• Severe barrier destruction', functionalState: ['inflamed', 'destroyed', 'leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/antigbm-light1.jpg?alt=media&token=d5806df2-6036-4d00-8017-0ad48da8bf89'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/anti-gbm-em1.png?alt=media&token=241a2dfb-a84b-48cc-aad9-442cfaa24605'] },
    anca: { key: 'anca', type: 'disease', name: 'ANCA Vasculitis', description: 'A small-vessel vasculitis associated with anti-neutrophil cytoplasmic antibodies (ANCA)...', implications: ['Rapidly progressive glomerulonephritis (RPGN).', 'Often has systemic symptoms (fever, malaise).', 'High risk of kidney failure if not treated promptly.'], animation: ['cellular_crescent', 'necrosis', 'hematuria', 'proteinuria', 'barrier_destruction'], proteinuriaLevel: 'moderate', corpuscleChanges: '• Necrotizing crescents<br>• Pauci-immune (no deposits)', barrierChanges: '• Severe barrier destruction', functionalState: ['inflamed', 'destroyed', 'leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/anca-light1.jpg?alt=media&token=6f3e4532-bd51-4c7f-8334-31e7fd692e4e'] },
    ln1: { key: 'ln1', type: 'disease', name: 'Lupus Nephritis Class I', description: 'Minimal mesangial lupus nephritis...', implications: ['Clinically silent or very mild abnormalities.', 'Normal kidney function.', 'Immune deposits visible on IF/EM only.'], animation: ['mesangial_ic_deposits'], corpuscleChanges: '• Normal by light microscopy<br>• Mesangial immune deposits', barrierChanges: '• Normal barrier', functionalState: ['normal'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ln1-light1.png?alt=media&token=2aa33f17-2ab1-4053-9cd8-a4abdcbab355', 'https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ln1-light2.png?alt=media&token=070fa855-0886-4d35-8894-b4d6075d250d'] },
    ln2: { key: 'ln2', type: 'disease', name: 'Lupus Nephritis Class II', description: 'Mesangial proliferative lupus nephritis...', implications: ['Microscopic hematuria.', 'Sub-nephrotic proteinuria.', 'Generally good prognosis.'], animation: ['mesangial_hypercellularity', 'mesangial_ic_deposits', 'hematuria', 'proteinuria'], proteinuriaLevel: 'moderate', corpuscleChanges: '• Mesangial hypercellularity<br>• Mesangial immune deposits', barrierChanges: '• Normal barrier', functionalState: ['inflamed', 'leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ln2-light1.jpg?alt=media&token=281b9d2f-05de-4af0-b550-158d7e02d0d1'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ln2-em1.png?alt=media&token=cc60b129-a995-4fb9-9fac-7318627fe529'] },
    ln3: { key: 'ln3', type: 'disease', name: 'Lupus Nephritis Class III', description: 'Focal lupus nephritis. Affecting <50% of glomeruli...', implications: ['Hematuria and proteinuria are common.', 'Can progress to Class IV.', 'Requires aggressive immunosuppression.'], animation: ['endocapillary_hypercellularity_focal', 'subendothelial_deposits', 'ic_deposits', 'hematuria', 'proteinuria'], proteinuriaLevel: 'moderate', corpuscleChanges: '• Focal endocapillary proliferation<br>• Subendothelial deposits', barrierChanges: '• Subendothelial deposits', functionalState: ['inflamed', 'leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ln3-light1.jpg?alt=media&token=1e1169ba-d1bd-4a30-a119-2e4f9fa81434'] },
    ln4: { key: 'ln4', type: 'disease', name: 'Lupus Nephritis Class IV', description: 'Diffuse lupus nephritis. Affecting ≥50% of glomeruli...', implications: ['Nephritic syndrome, often with nephrotic-range proteinuria.', 'High risk of progression to kidney failure.', 'Requires aggressive immunosuppression.'], animation: ['endocapillary_hypercellularity', 'subendothelial_deposits', 'ic_deposits', 'hematuria', 'proteinuria'], proteinuriaLevel: 'massive', corpuscleChanges: '• Diffuse endocapillary proliferation<br>• "Wire loop" lesions', barrierChanges: '• Widespread subendothelial deposits', functionalState: ['inflamed', 'leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ln4-light1.jpg?alt=media&token=18681ae6-0bf8-4b93-b3c4-f62bf29c5306'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ln4-em1.png?alt=media&token=84a5cd7d-cfe6-407c-9595-a9e8ce46bf18'] },
    ln5: { key: 'ln5', type: 'disease', name: 'Lupus Nephritis Class V', description: 'Membranous lupus nephritis. Similar to primary membranous nephropathy...', implications: ['Presents as nephrotic syndrome.', 'Often has a more indolent course than Class III/IV.'], animation: ['subepithelial_deposits', 'proteinuria'], proteinuriaLevel: 'massive', corpuscleChanges: '• Diffuse GBM thickening', barrierChanges: '• Subepithelial deposits', functionalState: ['leaky'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ln5-light1.jpg?alt=media&token=d1b85f19-42cd-4214-869b-034d184cb9a9'], emImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ln5-em1.png?alt=media&token=78cdb1c9-4a14-490d-b498-84ffbb5ff860'] },
    ln6: { key: 'ln6', type: 'disease', name: 'Lupus Nephritis Class VI', description: 'Advanced sclerotic lupus nephritis. >90% of glomeruli are globally sclerosed...', implications: ['End-stage renal disease.', 'Proteinuria may be absent if filtration has ceased.'], animation: ['global_sclerosis'], corpuscleChanges: '• Global glomerulosclerosis', barrierChanges: '• Obliterated structures', functionalState: ['scarred'], corpuscleImageUrl: ['https://firebasestorage.googleapis.com/v0/b/crrt-1b16b.firebasestorage.app/o/ln6-light1.jpg?alt=media&token=8524baa1-700b-4ecd-8496-79a7acc0615f'] },
};

// --- SVG & ANIMATION COMPONENTS ---

const StaticParticle = ({ cx, cy, isRBC }) => {
    const randomDelay = useMemo(() => Math.random() * 0.5, []);
    const randomXJitter = useMemo(() => (Math.random() - 0.5) * 10, []);
    
    return (
        <motion.circle
            cx={cx}
            cy={cy}
            r={isRBC ? 12 : 8}
            fill={isRBC ? "rgba(239, 68, 68, 0.6)" : "rgba(251, 191, 36, 0.7)"}
            stroke={isRBC ? '#b91c1c' : 'none'}
            strokeWidth="2"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
                opacity: 1, 
                scale: 1,
                x: [0, randomXJitter, 0]
            }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ 
                delay: randomDelay,
                x: {
                    duration: Math.random() * 2 + 2,
                    repeat: Infinity,
                    repeatType: "mirror",
                    ease: "easeInOut"
                }
            }}
        />
    );
};

const GlomerulusVisualization = ({ pathology, onViewSlide }) => {
    const { corpuscleChanges, animation, key, corpuscleImageUrl } = pathology;
    const hasAnimation = (anim) => animation === anim || (Array.isArray(animation) && animation.includes(anim));

    const animStates = {
        initial: { scale: 1, opacity: 1, fill: '#c084fc', stroke: '#9333ea' },
        hypercellularity: { scale: 1.3, fill: '#fca5a5' },
        sclerosis: { scale: 0.1, opacity: 0.2, fill: '#d1d5db' }
    };

    const depositData = {
        iga_deposits: [
            { x: 280, y: 280, label: 'IgA' },
            { x: 320, y: 320, label: 'IgA' },
            { x: 330, y: 260, label: 'IgA' },
            { x: 270, y: 330, label: 'IgA' },
        ],
        ic_deposits: [
            { x: 290, y: 290, label: 'IgG' },
            { x: 310, y: 310, label: 'IgM' },
            { x: 200, y: 250, label: 'C3' },
            { x: 400, y: 380, label: 'C1q' },
            { x: 400, y: 220, label: 'IgG' },
        ],
        mesangial_ic_deposits: [
            { x: 290, y: 290, label: 'IgG' },
            { x: 310, y: 310, label: 'C3' },
            { x: 300, y: 260, label: 'IgM' },
        ],
        c3_deposits: [
            { x: 300, y: 300, label: 'C3' },
            { x: 220, y: 220, label: 'C3' },
            { x: 380, y: 380, label: 'C3' },
            { x: 220, y: 380, label: 'C3' },
            { x: 380, y: 220, label: 'C3' },
        ],
        starry_sky_c3: [
            { x: 250, y: 230 }, { x: 380, y: 190 }, { x: 450, y: 300 },
            { x: 150, y: 300 }, { x: 210, y: 410 }, { x: 390, y: 410 },
            { x: 300, y: 280 }, { x: 320, y: 330 }, { x: 270, y: 350 },
            { x: 420, y: 250 }, { x: 180, y: 380 }, { x: 260, y: 180 },
        ]
    };

    const legendItems = useMemo(() => {
        const items = [
            { label: 'Capillary Lumen', color: 'white', border: true },
            { label: 'Basement Memb.', color: '#f87171' },
            { label: 'Podocytes', color: 'rgba(96, 165, 250, 0.3)' },
            { label: 'Mesangium', color: '#d8b4fe' },
            { label: "Bowman's Space", color: '#fefce8', border: true },
        ];

        if (hasAnimation('fsgs') || hasAnimation('global_sclerosis')) {
            items.push({ label: 'Sclerosis', color: '#f472b6' });
        }
        if (hasAnimation('cellular_crescent')) {
            items.push({ label: 'Crescent', color: 'rgba(134, 239, 172, 0.5)' });
        }
        if (hasAnimation('iga_deposits')) {
            items.push({ label: 'IgA Deposits', color: '#ec4899' });
        }
        if (hasAnimation('ic_deposits') || hasAnimation('mesangial_ic_deposits')) {
            items.push({ label: 'Immune Complexes', color: '#14b8a6' });
        }
         if (hasAnimation('c3_deposits') || hasAnimation('starry_sky_c3')) {
            items.push({ label: 'C3 Deposits', color: '#22c55e' });
        }
        if (hasAnimation('tma_thrombi')) {
            items.push({ label: 'Fibrin Thrombi', color: '#e11d48' });
        }
        if (hasAnimation('linear_deposits')) {
            items.push({ label: 'Linear IgG Deposits', color: '#a3e635' });
        }
        if (hasAnimation('necrosis')) {
            items.push({ label: 'Fibrinoid Necrosis', color: '#fb7185' });
        }
        if (hasAnimation('endocapillary_hypercellularity') || hasAnimation('endocapillary_hypercellularity_focal')) {
            items.push({ label: 'Endocapillary Cells', color: '#4F2683' });
        }

        return items;
    }, [key, animation]);

    return (
        <div className="w-full flex flex-col items-center">
             <div className="flex justify-center items-center gap-4 mb-3">
                <h3 className="text-2xl font-semibold text-center text-gray-800">Glomerular Corpuscle</h3>
                {corpuscleImageUrl && (
                    <button
                        onClick={() => onViewSlide(corpuscleImageUrl)}
                        className="text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1 p-1 rounded-md hover:bg-blue-100"
                    >
                         <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                        View Slide
                    </button>
                )}
            </div>
            <div className="w-full grid grid-cols-3 gap-4">
                <div className="col-span-2">
                    <motion.svg viewBox="0 0 600 600" className="w-full h-auto border rounded-xl bg-indigo-50 shadow-inner">
                        <defs>
                            <radialGradient id="mesangial-grad" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                                <stop offset="0%" style={{stopColor: '#e9d5ff'}} />
                                <stop offset="100%" style={{stopColor: '#d8b4fe'}} />
                            </radialGradient>
                             <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                                <feMerge>
                                    <feMergeNode in="coloredBlur"/>
                                    <feMergeNode in="SourceGraphic"/>
                                </feMerge>
                            </filter>
                             <filter id="brightGlow" x="-50%" y="-50%" width="200%" height="200%">
                                <feGaussianBlur stdDeviation="8" result="coloredBlur"/>
                                <feMerge>
                                    <feMergeNode in="coloredBlur"/>
                                    <feMergeNode in="SourceGraphic"/>
                                </feMerge>
                            </filter>
                            <pattern id="crescent-pattern" patternUnits="userSpaceOnUse" width="20" height="20">
                                <circle cx="10" cy="10" r="8" fill="rgba(134, 239, 172, 0.5)"/>
                                <circle cx="10" cy="10" r="3" fill="rgba(22, 163, 74, 0.7)"/>
                            </pattern>
                        </defs>
                        <motion.g>
                            <motion.path 
                                d="M 570,300 A 270,270 0 1 1 30,300 A 270,270 0 0 1 570,300 Z" 
                                fill="#fefce8" stroke="#fde047" strokeWidth="6"
                            />
                             <AnimatePresence>
                                {hasAnimation('cellular_crescent') && (
                                    <motion.path
                                        d="M 570,300 A 270,270 0 0 1 30,300 L 200,300 A 100,100 0 0 0 500,300 Z"
                                        fill="url(#crescent-pattern)"
                                        stroke="#4ade80" strokeWidth="3"
                                        initial={{ pathLength: 0, opacity: 0 }}
                                        animate={{ pathLength: 1, opacity: 0.8 }}
                                        exit={{ pathLength: 0, opacity: 0 }}
                                        transition={{ duration: 1, ease: "easeInOut" }}
                                    />
                                )}
                            </AnimatePresence>
                            <motion.g transformOrigin="300 300" animate={hasAnimation('cellular_crescent') ? { scale: 0.7, x: 15, y: 50 } : { scale: 1.2, x: 0, y: 0 }} transition={{ duration: 1 }}>
                                <g transform="scale(1.2)" transform-origin="300 300">
                                    <motion.g animate={hasAnimation('mesangial_hypercellularity') ? { scale: 1.2 } : { scale: 1 }} transition={{ duration: 0.8 }}>
                                        <path d="M300,220 C250,220 200,250 200,300 C200,350 250,380 300,380 C350,380 400,350 400,300 C400,250 350,220 300,220 Z" fill="url(#mesangial-grad)" stroke="#c084fc" strokeWidth="2"/>
                                        {/* Mesangial Cells */}
                                        {[...Array(5)].map((_, i) => (
                                            <motion.circle key={i} cx={300 + 40*Math.cos(i*2*Math.PI/5)} cy={300 + 40*Math.sin(i*2*Math.PI/5)} r="12"
                                                variants={animStates}
                                                animate={hasAnimation('mesangial_hypercellularity') ? 'hypercellularity' : 'initial'}
                                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                            />
                                        ))}
                                    </motion.g>
                                    {/* Capillary Loops */}
                                    {[...Array(6)].map((_, i) => {
                                        const isHypercellular = hasAnimation('endocapillary_hypercellularity') || (hasAnimation('endocapillary_hypercellularity_focal') && i < 3);
                                        return (
                                        <motion.g key={i} animate={hasAnimation('mesangial_hypercellularity') ? {scale: 0.9} : {scale: 1}} transition={{duration: 0.8}}>
                                            <motion.ellipse cx={300+110*Math.cos(i*Math.PI/3)} cy={300+110*Math.sin(i*Math.PI/3)} rx="65" ry="45"
                                                initial={{ strokeWidth: 8, stroke: '#f87171' }}
                                                animate={{
                                                    strokeWidth: hasAnimation('subepithelial_deposits') || hasAnimation('membranous') || hasAnimation('post_infectious_humps') ? 16 : hasAnimation('thinning_gbm') ? 3 : 8,
                                                    stroke: hasAnimation('subepithelial_deposits') || hasAnimation('membranous') || hasAnimation('post_infectious_humps') ? '#ef4444' : '#f87171'
                                                }}
                                                transition={{ duration: 0.8 }}
                                            />
                                            <motion.ellipse cx={300+110*Math.cos(i*Math.PI/3)} cy={300+110*Math.sin(i*Math.PI/3)} 
                                                rx={isHypercellular ? 20 : 57} 
                                                ry={isHypercellular ? 15 : 37}
                                                fill="white" stroke="#ef4444"
                                                initial={{strokeWidth: 2}}
                                                animate={{
                                                    strokeWidth: isHypercellular ? 6 : 2,
                                                }}
                                                transition={{ duration: 0.8 }}
                                            />
                                            <AnimatePresence>
                                                {isHypercellular && [...Array(4)].map((_, cellIndex) => {
                                                    const angle = Math.random() * 2 * Math.PI;
                                                    const radiusX = 20 + Math.random() * 25;
                                                    const radiusY = 15 + Math.random() * 20;
                                                    const cellX = 300 + 110 * Math.cos(i * Math.PI / 3) + radiusX * Math.cos(angle);
                                                    const cellY = 300 + 110 * Math.sin(i * Math.PI / 3) + radiusY * Math.sin(angle);
                                                    return (
                                                        <motion.circle
                                                            key={`cell-${i}-${cellIndex}`}
                                                            cx={cellX}
                                                            cy={cellY}
                                                            r="5"
                                                            fill="#4F2683"
                                                            initial={{ opacity: 0, scale: 0 }}
                                                            animate={{ opacity: 1, scale: 1 }}
                                                            exit={{ opacity: 0, scale: 0 }}
                                                            transition={{ delay: cellIndex * 0.1 + i*0.05 }}
                                                        />
                                                    )
                                                })}
                                            </AnimatePresence>
                                            <AnimatePresence>
                                                {(hasAnimation('tram_tracking')) && (
                                                    <motion.ellipse
                                                        cx={300+110*Math.cos(i*Math.PI/3)}
                                                        cy={300+110*Math.sin(i*Math.PI/3)}
                                                        rx="70"
                                                        ry="50"
                                                        fill="none"
                                                        stroke="#f87171"
                                                        strokeWidth="4"
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        exit={{ opacity: 0 }}
                                                        transition={{ duration: 0.8 }}
                                                    />
                                                )}
                                            </AnimatePresence>
                                             {(hasAnimation('fsgs') && (i === 1 || i === 4)) && <motion.ellipse cx={300+110*Math.cos(i*Math.PI/3)} cy={300+110*Math.sin(i*Math.PI/3)} rx="65" ry="45" fill="#f472b6" initial={{opacity:0}} animate={{opacity:0.6}} transition={{duration:0.5}}/>}
                                             {hasAnimation('global_sclerosis') && <motion.ellipse cx={300+110*Math.cos(i*Math.PI/3)} cy={300+110*Math.sin(i*Math.PI/3)} rx="65" ry="45" fill="#a1a1aa" initial={{opacity:0}} animate={{opacity:0.8}} transition={{duration:0.5, delay: i * 0.1}}/>}
                                             {hasAnimation('tma_thrombi') && (i % 2 === 0) && <motion.path d={`M ${300+110*Math.cos(i*Math.PI/3)-20} ${300+110*Math.sin(i*Math.PI/3)} q 20 -20 40 0`} stroke="#e11d48" strokeWidth="6" fill="none" initial={{pathLength: 0}} animate={{pathLength: 1}} transition={{duration: 0.5, delay: i*0.1}} />}
                                             {hasAnimation('necrosis') && (i === 1) && <motion.ellipse cx={300+110*Math.cos(i*Math.PI/3)} cy={300+110*Math.sin(i*Math.PI/3)} rx="65" ry="45" fill="#fb7185" initial={{opacity:0}} animate={{opacity:0.7}} transition={{duration:0.5}}/>}
                                             {hasAnimation('linear_deposits') && <motion.ellipse cx={300+110*Math.cos(i*Math.PI/3)} cy={300+110*Math.sin(i*Math.PI/3)} rx="65" ry="45" fill="none" stroke="#a3e635" strokeWidth="5" filter="url(#brightGlow)" initial={{pathLength: 0}} animate={{pathLength: 1}} transition={{duration: 1.5, delay: i*0.1}} />}
                                        </motion.g>
                                    )})}
                                    {/* Podocyte layer */}
                                    {[...Array(6)].map((_, i) => (
                                        <ellipse key={i} cx={300+110*Math.cos(i*Math.PI/3)} cy={300+110*Math.sin(i*Math.PI/3)} rx="75" ry="55" fill="rgba(96, 165, 250, 0.3)" stroke="#60a5fa" strokeWidth="2" />
                                    ))}
                                    <g className="deposits-group">
                                        <AnimatePresence>
                                            {hasAnimation('iga_deposits') && depositData.iga_deposits.map((dep, i) => (
                                                <motion.text
                                                    key={`iga-${i}`}
                                                    x={dep.x}
                                                    y={dep.y}
                                                    className="font-bold text-lg fill-pink-600"
                                                    filter="url(#glow)"
                                                    initial={{ opacity: 0, scale: 0.5 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.5 }}
                                                    transition={{ delay: i * 0.1 }}
                                                >
                                                    {dep.label}
                                                </motion.text>
                                            ))}
                                            {(hasAnimation('ic_deposits') || hasAnimation('mesangial_ic_deposits')) && depositData[hasAnimation('ic_deposits') ? 'ic_deposits' : 'mesangial_ic_deposits'].map((dep, i) => (
                                                <motion.text
                                                    key={`ic-${i}`}
                                                    x={dep.x}
                                                    y={dep.y}
                                                    className="font-bold text-lg fill-teal-500"
                                                    filter="url(#glow)"
                                                    initial={{ opacity: 0, scale: 0.5 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.5 }}
                                                    transition={{ delay: i * 0.1 }}
                                                >
                                                    {dep.label}
                                                </motion.text>
                                            ))}
                                            {hasAnimation('c3_deposits') && depositData.c3_deposits.map((dep, i) => (
                                                <motion.text
                                                    key={`c3-${i}`}
                                                    x={dep.x}
                                                    y={dep.y}
                                                    className="font-bold text-lg fill-green-500"
                                                    filter="url(#glow)"
                                                    initial={{ opacity: 0, scale: 0.5 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.5 }}
                                                    transition={{ delay: i * 0.1 }}
                                                >
                                                    {dep.label}
                                                </motion.text>
                                            ))}
                                             {hasAnimation('starry_sky_c3') && depositData.starry_sky_c3.map((dep, i) => (
                                                <motion.text
                                                    key={`starry-${i}`}
                                                    x={dep.x}
                                                    y={dep.y}
                                                    className="font-bold text-sm fill-green-400"
                                                    filter="url(#glow)"
                                                    initial={{ opacity: 0, scale: 0.5 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.5 }}
                                                    transition={{ delay: i * 0.02 }}
                                                >
                                                    C3
                                                </motion.text>
                                            ))}
                                        </AnimatePresence>
                                    </g>
                                </g>
                            </motion.g>
                        </motion.g>
                    </motion.svg>
                </div>
                <div className="col-span-1 text-sm">
                    <h4 className="font-semibold mb-2 text-gray-900">Legend</h4>
                    <div className="space-y-1">
                        {legendItems.map(item => (
                             <div key={item.label} className="flex items-center">
                                <div className={`w-4 h-4 rounded-sm mr-2 ${item.border ? 'border' : ''}`} style={{ backgroundColor: item.color }}></div>
                                {item.label}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="summary-box text-center font-semibold text-gray-800 mt-3 p-3 bg-gray-100 rounded-lg w-full min-h-[54px]" dangerouslySetInnerHTML={{ __html: corpuscleChanges }}></div>
        </div>
    );
};

const FiltrationBarrierVisualization = ({ pathology, onViewSlide }) => {
    const { barrierChanges, animation, key, proteinuriaLevel, emImageUrl } = pathology;
    const hasAnimation = (anim) => animation === anim || (Array.isArray(animation) && animation.includes(anim));

    const footProcessVariants = {
        normal: { d: "M 0,50 L 10,-50 L 30,-50 L 40,50 Z", fill: "#60a5fa", stroke: "#2563eb" },
        effaced: { d: "M 0,50 L -10,0 L 50,0 L 40,50 Z", fill: "#93c5fd", stroke: "#3b82f6" }
    };

    const legendItems = useMemo(() => {
        const items = [
            { label: 'Capillary Lumen', color: '#fee2e2', border: true },
            { label: 'Fenestrations', color: 'white', border: true },
            { label: 'GBM', color: '#dc2626' },
            { label: 'Podocyte', color: '#60a5fa' },
            { label: "Bowman's Space", color: '#fefce8', border: true },
        ];

        if (hasAnimation('subepithelial_deposits') || hasAnimation('subendothelial_deposits') || hasAnimation('post_infectious_humps')) {
            items.push({ label: 'Immune Deposits', color: '#27272a' });
        }
        if (hasAnimation('ddd_band')) {
            items.push({ label: 'Dense Deposits', color: '#374151' });
        }
        if (hasAnimation('proteinuria')) {
            items.push({ label: 'Protein', color: 'rgba(251, 191, 36, 0.7)' });
        }
        if (hasAnimation('hematuria')) {
            items.push({ label: 'RBC', color: 'rgba(239, 68, 68, 0.6)' });
        }
        if (hasAnimation('tma_thrombi')) {
            items.push({ label: 'Widened Subendothelial Space', color: '#fbcfe8' });
        }

        return items;
    }, [key, animation]);

    return (
        <div className="w-full flex flex-col items-center">
            <div className="flex justify-center items-center gap-4 mb-3">
                <h3 className="text-2xl font-semibold text-center text-gray-800">Filtration Barrier (EM View)</h3>
                 {emImageUrl && (
                    <button
                        onClick={() => onViewSlide(emImageUrl)}
                        className="text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1 p-1 rounded-md hover:bg-blue-100"
                    >
                         <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                        View Slide
                    </button>
                )}
            </div>
            <div className="w-full grid grid-cols-3 gap-4">
                <div className="col-span-2">
                    <div className="w-full relative" style={{ paddingTop: '75%' }}>
                        <svg viewBox="0 0 800 600" className="absolute top-0 left-0 w-full h-full rounded-xl border shadow-inner">
                            <rect x="0" y="0" width="800" height="250" fill="#fee2e2" />
                            <AnimatePresence>
                                {hasAnimation('tma_thrombi') && <motion.rect x="0" y="250" width="800" height="60" fill="#fbcfe8" initial={{height: 0}} animate={{height: 60}} exit={{height: 0}} />}
                            </AnimatePresence>
                            <rect x="0" y="310" width="800" height="290" fill="#fefce8" />
                            <text x="400" y="50" className="text-3xl font-bold fill-red-800" textAnchor="middle">Capillary Lumen</text>
                            <text x="400" y="550" className="text-3xl font-bold fill-yellow-800" textAnchor="middle">Bowman's Space</text>
                            
                            {/* Barrier Layers */}
                            <g>
                                {/* Fenestrations */}
                                {[...Array(20)].map((_, i) => (
                                    <motion.circle 
                                        key={i} 
                                        cx={40 + i * 40} 
                                        cy="240" 
                                        r={10} 
                                        fill="white"
                                        animate={{ r: hasAnimation('endocapillary_hypercellularity') || hasAnimation('endocapillary_hypercellularity_focal') ? 5 : 10 }}
                                    />
                                ))}
                                
                                {/* GBM */}
                                <motion.g id="gbm-group" 
                                    animate={{ 
                                        scaleY: hasAnimation('subepithelial_deposits') || hasAnimation('membranous') || hasAnimation('ic_mpgn') || hasAnimation('post_infectious_humps') || hasAnimation('tram_tracking') ? 1.5 : hasAnimation('thinning_gbm') ? 0.4 : 1, 
                                        y: hasAnimation('subepithelial_deposits') || hasAnimation('membranous') || hasAnimation('post_infectious_humps') || hasAnimation('ic_mpgn') || hasAnimation('tram_tracking') ? -15 : 0 
                                    }} 
                                    transition={{duration: 0.8}}
                                >
                                    <rect id="gbm" x="0" y="250" width="800" height="60" fill="#dc2626"/>
                                    <AnimatePresence>
                                        {hasAnimation('ddd_band') && (
                                            <motion.rect
                                                x="0"
                                                y="265"
                                                width="800"
                                                height="30"
                                                fill="#374151"
                                                initial={{ scaleX: 0 }}
                                                animate={{ scaleX: 1 }}
                                                exit={{ scaleX: 0 }}
                                                transition={{ duration: 0.8, ease: 'easeInOut' }}
                                                style={{ transformOrigin: '50% 50%' }}
                                            />
                                        )}
                                    </AnimatePresence>
                                </motion.g>

                                {/* Subendothelial Deposits */}
                                <AnimatePresence>
                                {hasAnimation('subendothelial_deposits') && [...Array(10)].map((_, i) => (
                                    <motion.path key={i} d={`M ${30 + i * 80}, 250 a 25,15 0 1,0 50,0`} fill="#27272a" stroke="#000"
                                        initial={{ scaleY: 0, opacity: 0 }}
                                        animate={{ scaleY: 1, opacity: 1 }}
                                        exit={{ scaleY: 0, opacity: 0 }}
                                        transition={{ duration: 0.5, delay: i * 0.05, ease: 'backOut' }}
                                        style={{transformOrigin: '50% 0%'}}
                                    />
                                ))}
                                </AnimatePresence>

                                {/* Subepithelial Deposits / Spikes (for Membranous) */}
                                 <AnimatePresence>
                                {(hasAnimation('subepithelial_deposits') || hasAnimation('membranous')) && [...Array(10)].map((_, i) => (
                                    <g key={i}>
                                    <motion.path d={`M ${30 + i * 80}, 310 a 25,15 0 1,1 50,0`} fill="#27272a" stroke="#000"
                                        initial={{ scaleY: 0, opacity: 0 }}
                                        animate={{ scaleY: 1, opacity: 1 }}
                                        exit={{ scaleY: 0, opacity: 0 }}
                                        transition={{ duration: 0.5, delay: i * 0.05, ease: 'backOut' }}
                                        style={{transformOrigin: '50% 100%'}}
                                    />
                                     <motion.path d={`M ${30 + i * 80}, 310 l 25 20 l 25 -20`} fill="none" stroke="#dc2626" strokeWidth="4"
                                        initial={{ y: 0, opacity: 0 }}
                                        animate={{ y: 20, opacity: 1 }}
                                        exit={{ y: 0, opacity: 0 }}
                                        transition={{ duration: 0.5, delay: 0.3 + i * 0.05, ease: 'circOut' }}
                                    />
                                    </g>
                                ))}
                                </AnimatePresence>
                                
                                {/* Post-Infectious "Humps" */}
                                <AnimatePresence>
                                {hasAnimation('post_infectious_humps') && [...Array(8)].map((_, i) => (
                                    <motion.circle 
                                        key={i}
                                        cx={60 + i * 100}
                                        cy={310}
                                        r={30}
                                        fill="#27272a"
                                        stroke="#000"
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        exit={{ scale: 0, opacity: 0 }}
                                        transition={{ duration: 0.5, delay: i * 0.05, ease: 'backOut' }}
                                    />
                                ))}
                                </AnimatePresence>

                                {/* Podocytes */}
                                <g>
                                    {[...Array(10)].map((_, i) => (
                                        <motion.g key={i} transform={`translate(${50 + i * 80}, 310)`}
                                            animate={{ opacity: hasAnimation('barrier_destruction') && i % 3 === 0 ? 0.2 : 1 }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            <motion.path
                                                variants={footProcessVariants}
                                                initial="normal"
                                                animate={
                                                    (hasAnimation('fsgs') && i > 2 && i < 7) || hasAnimation('podocyte_effacement') || hasAnimation('minimal_change')
                                                    ? "effaced" 
                                                    : "normal"
                                                }
                                                transition={{ duration: 0.8, type: 'spring' }}
                                            />
                                        </motion.g>
                                    ))}
                                </g>
                                {/* Barrier Breaks */}
                                <g>
                                    <AnimatePresence>
                                        {hasAnimation('barrier_destruction') && [
                                            {x: 100, width: 80}, {x: 400, width: 120}, {x: 650, width: 70}
                                        ].map((br, i) => (
                                            <motion.rect key={`break-${i}`} x={br.x} y="250" width={br.width} height="60" fill="#fefce8"
                                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                            />
                                        ))}
                                    </AnimatePresence>
                                </g>
                            </g>
                            {/* Static Particles Rendered Last to be on Top */}
                            <g>
                                <AnimatePresence>
                                    {hasAnimation('proteinuria') && Array.from({ length: proteinuriaLevel === 'massive' ? 80 : 15 }).map((_, i) => (
                                        <StaticParticle key={`p-static-${i}`} cx={Math.random() * 700 + 50} cy={400 + Math.random() * 100} />
                                    ))}
                                    {hasAnimation('hematuria') && Array.from({ length: 10 }).map((_, i) => (
                                        <StaticParticle key={`h-static-${i}`} cx={Math.random() * 700 + 50} cy={400 + Math.random() * 100} isRBC={true} />
                                    ))}
                                </AnimatePresence>
                            </g>
                        </svg>
                    </div>
                </div>
                <div className="col-span-1 text-sm">
                    <h4 className="font-semibold mb-2 text-gray-900">Legend</h4>
                    <div className="space-y-1">
                        {legendItems.map(item => (
                             <div key={item.label} className="flex items-center">
                                <div className={`w-4 h-4 rounded-sm mr-2 ${item.border ? 'border' : ''}`} style={{ backgroundColor: item.color }}></div>
                                {item.label}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
             <div className="summary-box text-center font-semibold text-gray-800 mt-3 p-3 bg-gray-100 rounded-lg w-full min-h-[54px]" dangerouslySetInnerHTML={{ __html: barrierChanges }}></div>
        </div>
    );
};


const FunctionalOutcomeVisualization = ({ pathology }) => {
    const { functionalState, proteinuriaLevel, key } = pathology;
    const hasState = (state) => functionalState.includes(state);

    const isClogged = hasState('inflamed') || hasState('clogged');
    const isDestroyed = hasState('destroyed') || hasState('scarred');
    const hasHematuria = hasState('inflamed') || hasState('fragile') || hasState('destroyed') || hasState('clogged');
    
    // Special composite states for visuals
    const isFSGS = key === 'fsgs';
    const isSevereRPGN = key === 'anca' || key === 'anti_gbm';

    const legendItems = useMemo(() => {
        const items = [{ label: 'Glomerulus', color: '#60a5fa' }];
        if (hasState('leaky')) items.push({ label: 'Protein', color: '#facc15' });
        if (hasHematuria) items.push({ label: 'RBCs (Hematuria)', color: '#ef4444' });
        return items;
    }, [functionalState, hasHematuria]);
    
    const functionalLabels = useMemo(() => {
        if (key === 'fsgs') return 'Normal/Scarred & Leaky';
        if (key === 'anca' || key === 'anti_gbm') return 'Destroyed/Inflamed & Leaky';
        if (key === 'cellular_crescent') return 'Destroyed/Inflamed & Leaky';
    
        if (functionalState.includes('normal')) return 'Normal';
        
        const sortedStates = [...functionalState].sort();
        return sortedStates
            .map(s => s.charAt(0).toUpperCase() + s.slice(1))
            .join(' & ');
    }, [functionalState, key]);


    return (
        <div className="w-full flex flex-col items-center mt-8">
            <h3 className="text-2xl font-semibold mb-3 text-center text-gray-800">Functional Outcome</h3>
            <div className="w-full grid grid-cols-3 gap-4">
                <div className="col-span-2">
                    <svg viewBox="0 0 400 300" className="w-full h-auto border rounded-xl bg-gray-50 shadow-inner">
                        <defs>
                            <pattern id="cracks" patternUnits="userSpaceOnUse" width="20" height="20">
                                <path d="M 0 0 L 20 20 M 10 -10 L 10 10 M -10 10 L 10 10" stroke="#4b5563" strokeWidth="2" />
                            </pattern>
                        </defs>
                        
                        {/* Glomerulus Body */}
                        <g>
                            {isFSGS ? (
                                <>
                                    {/* Normal Half */}
                                    <path d="M 200 70 A 60 60 0 0 1 200 190 L 200 70 Z" fill="#60a5fa" stroke="#3b82f6" strokeWidth="5" />
                                    {/* Scarred Half */}
                                    <path d="M 200 70 A 60 60 0 0 0 200 190 L 200 70 Z" fill="#9ca3af" stroke="#6b7280" strokeWidth="5" />
                                    <path d="M 200 70 A 60 60 0 0 0 200 190 L 200 70 Z" fill="url(#cracks)" style={{ opacity: 0.5 }} />
                                </>
                            ) : isSevereRPGN ? (
                                <>
                                    {/* Inflamed Half */}
                                    <path d="M 200 70 A 60 60 0 0 1 200 190 L 200 70 Z" fill="#f9a8d4" stroke="#f472b6" strokeWidth="5" />
                                    {/* Destroyed Half */}
                                    <path d="M 200 70 A 60 60 0 0 0 200 190 L 200 70 Z" fill="#9ca3af" stroke="#6b7280" strokeWidth="5" />
                                    <path d="M 200 70 A 60 60 0 0 0 200 190 L 200 70 Z" fill="url(#cracks)" style={{ opacity: 0.5 }} />
                                </>
                            ) : (
                                <motion.circle 
                                    cx="200" 
                                    cy="130" 
                                    r="60" 
                                    stroke="#3b82f6" 
                                    strokeWidth="5"
                                    initial={{ fill: "#60a5fa" }}
                                    animate={{ 
                                        fill: isClogged ? '#f9a8d4' : isDestroyed ? '#9ca3af' : '#60a5fa',
                                        stroke: isClogged ? '#f472b6' : isDestroyed ? '#6b7280' : '#3b82f6',
                                    }}
                                    transition={{ duration: 0.8 }}
                                />
                            )}
                            
                            <AnimatePresence>
                            {isDestroyed && !isFSGS && !isSevereRPGN && (
                                <motion.circle
                                    cx="200" cy="130" r="60" fill="url(#cracks)"
                                    initial={{ opacity: 0 }} animate={{ opacity: 0.5 }} exit={{ opacity: 0 }}
                                />
                            )}
                            </AnimatePresence>
                        </g>
                        
                        {/* Particles and Clinical Labels */}
                        <g>
                           <AnimatePresence>
                               {hasState('leaky') && (
                                   <g key="leaky-group">
                                    <motion.text x="30" y="50" className="font-bold fill-yellow-500" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>Proteinuria</motion.text>
                                    {Array.from({length: proteinuriaLevel === 'massive' ? 12 : 5}).map((_, i) => (
                                         <motion.circle key={`protein-${i}`} cx={50 + (i * 12)} cy={70} r="5" fill="#facc15" initial={{opacity:0}} animate={{opacity:1, y: 30 + Math.random()*20}} exit={{opacity:0}} transition={{delay: i * 0.1}}/>
                                    ))}
                                   </g>
                               )}
                               { hasHematuria && (
                                   <g key="bleeding-group">
                                     <motion.text x="250" y="50" className="font-bold fill-red-500" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>Hematuria</motion.text>
                                     {Array.from({length: 5}).map((_, i) => (
                                         <motion.circle key={`rbc-${i}`} cx={270 + (i * 12)} cy={70} r="6" fill="#ef4444" initial={{opacity:0}} animate={{opacity:1, y: 50 + Math.random()*20}} exit={{opacity:0}} transition={{delay: 0.5 + i * 0.1}}/>
                                     ))}
                                   </g>
                               )}
                                {(isClogged || isDestroyed) && (
                                    <motion.text key="gfr-text" x="150" y="30" className="font-bold fill-purple-500" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>Reduced GFR</motion.text>
                                )}
                           </AnimatePresence>
                        </g>
                    </svg>
                </div>
                 <div className="col-span-1 text-sm">
                    <h4 className="font-semibold mb-2 text-gray-900">Legend</h4>
                    <div className="space-y-1">
                       {legendItems.map(item => (
                             <div key={item.label} className="flex items-center">
                                <div className={`w-4 h-4 rounded-sm mr-2 ${item.border ? 'border' : ''}`} style={{ backgroundColor: item.color }}></div>
                                {item.label}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
             <div className="summary-box text-center font-semibold text-gray-800 mt-3 p-3 bg-gray-100 rounded-lg w-full min-h-[54px]">
                <p>• <strong>{functionalLabels}</strong></p>
            </div>
        </div>
    );
};

const ViewToggle = ({ viewMode, setViewMode }) => {
    return (
        <div className="flex p-1 bg-gray-200 rounded-full">
            <button
                onClick={() => setViewMode('histology')}
                className={`px-6 py-2 rounded-full font-semibold transition-colors ${
                    viewMode === 'histology' ? 'bg-white text-[#4F2683] shadow' : 'text-gray-600'
                }`}
            >
                Histology
            </button>
            <button
                onClick={() => setViewMode('function')}
                className={`px-6 py-2 rounded-full font-semibold transition-colors ${
                    viewMode === 'function' ? 'bg-white text-[#4F2683] shadow' : 'text-gray-600'
                }`}
            >
                Function
            </button>
        </div>
    );
};


const ControlsPanel = ({ selectedKey, onPathologyChange }) => {
    const renderButton = (item) => (
        <ControlButton
            key={item.key}
            item={item}
            isSelected={selectedKey === item.key}
            onClick={() => onPathologyChange(item.key)}
        />
    );

    return (
        <div className="lg:col-span-1 bg-white p-6 rounded-2xl shadow-lg self-start">
            <div className="mb-6">
                 {renderButton(interactionData.normal)}
            </div>
            
            <h2 className="text-xl font-semibold mb-4 border-t pt-4 text-gray-800">Histologic Findings</h2>
            <div className="space-y-2 mb-6">
                {Object.values(interactionData).filter(item => item.type === 'finding').map(renderButton)}
            </div>
            <h2 className="text-xl font-semibold mb-4 border-b pb-2 text-gray-800">Disease States</h2>
            <div className="space-y-2">
                {Object.values(interactionData).filter(item => item.type === 'disease' && item.key !== 'normal').map(renderButton)}
            </div>
        </div>
    );
};

const ControlButton = ({ item, isSelected, onClick }) => (
    <motion.button
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
        className={`w-full text-left font-semibold py-3 px-4 rounded-xl transition-all duration-200 flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4F2683] ${
      isSelected 
        ? 'bg-[#4F2683] text-white shadow-lg' 
        : 'bg-white text-gray-700 border border-gray-200 hover:bg-indigo-50 hover:border-indigo-200'
    }`}
    >
        <div className={`w-2 h-2 rounded-full transition-colors duration-200 ${isSelected ? 'bg-white' : 'bg-gray-300'}`}></div>
        <span>{item.name}</span>
    </motion.button>
);


const InfoPanel = ({ pathology }) => {
    const { name, description, implications, type, key } = pathology;

    return (
        <div className="lg:col-span-5 bg-white p-8 rounded-2xl shadow-lg mt-8">
            <AnimatePresence mode="wait">
                <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                >
                    <h2 className="text-3xl font-bold mb-4 text-gray-900">{name}</h2>
                    <p className="text-lg text-gray-700 leading-relaxed">{description}</p>
                    <h3 className="text-2xl font-semibold text-gray-900 mt-6 mb-3">
                        {type === 'finding' ? 'Key Clinical Implications:' : 'Key Clinical Findings:'}
                    </h3>
                    <ul className="list-disc list-inside text-lg text-gray-700 space-y-2">
                        {implications.map((finding, index) => (
                            <li key={index}>{finding}</li>
                        ))}
                    </ul>
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

const FeedbackModal = ({ onClose, onSubmit }) => {
    const [q1, setQ1] = useState(0);
    const [q2, setQ2] = useState(0);
    const [q3, setQ3] = useState(0);
    const [role, setRole] = useState('');
    const [roleOther, setRoleOther] = useState('');
    const [practiceField, setPracticeField] = useState('');
    const [practiceFieldOther, setPracticeFieldOther] = useState('');
    const [country, setCountry] = useState('');
    const [comments, setComments] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        const feedbackData = {
            q1, q2, q3,
            role: role === 'Other' ? roleOther : role,
            practiceField: practiceField === 'Other' ? practiceFieldOther : practiceField,
            country,
            comments,
            timestamp: new Date(),
            sessionId: auth.currentUser?.uid || 'anonymous'
        };
        await onSubmit(feedbackData);
        setIsSubmitting(false);
        setIsSubmitted(true);
        setTimeout(onClose, 1500);
    };

    const LikertScale = ({ question, labels, value, onChange }) => (
        <div>
            <label className="block font-semibold text-gray-700 mb-2">{question}</label>
            <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500 w-1/4 text-left">{labels[0]}</span>
                <div className="flex justify-center items-center gap-2 sm:gap-4">
                {[1, 2, 3, 4, 5].map(v => (
                    <label key={v} className="flex flex-col items-center cursor-pointer">
                        <input type="radio" name={question} value={v} checked={value === v} onChange={() => onChange(v)} className="form-radio h-5 w-5 text-[#4F2683]" />
                        <span className="text-xs mt-1">{v}</span>
                    </label>
                ))}
                </div>
                <span className="text-xs text-gray-500 w-1/4 text-right">{labels[1]}</span>
            </div>
        </div>
    );

    return (
        <motion.div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}}>
            <motion.div className="bg-white rounded-lg shadow-2xl max-w-lg w-full flex flex-col max-h-[90vh] my-8" initial={{scale: 0.9, y: 20}} animate={{scale: 1, y: 0}} exit={{scale: 0.9, y: 20}} onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b">
                    <h3 className="text-2xl font-bold text-gray-800 text-center">Quick Feedback</h3>
                </div>
                {isSubmitted ? (
                    <div className="text-center py-8 px-6">
                        <svg className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-lg font-semibold text-gray-800 mt-4">Thank you for your feedback!</p>
                    </div>
                ) : (
                    <>
                        <div className="p-6 space-y-6 overflow-y-auto">
                            <p className="text-center text-gray-600">Your anonymous feedback will help refine this app and contribute to ongoing education innovation in nephrology.</p>
                            <LikertScale question="How useful was this simulator as a learning tool?" labels={["Not at all", "Very useful"]} value={q1} onChange={setQ1} />
                            <LikertScale question="How likely are you to use this simulator again?" labels={["Not at all", "Very likely"]} value={q2} onChange={setQ2} />
                            <LikertScale question="How likely are you to recommend this to someone else?" labels={["Not at all", "Very likely"]} value={q3} onChange={setQ3} />
                            
                            <div className="border-t border-gray-200 pt-6">
                                <h4 className="text-lg font-semibold text-gray-700 mb-4 text-center">About You (Optional)</h4>
                                <div className="space-y-4">
                                    <select value={role} onChange={e => setRole(e.target.value)} className="w-full bg-gray-100 text-gray-800 p-3 rounded-md border border-gray-300">
                                        <option value="">Select your Role...</option>
                                        <option>Medical Student</option>
                                        <option>Resident</option>
                                        <option>Fellow</option>
                                        <option>Nurse</option>
                                        <option>Nursing Student</option>
                                        <option>Physician (Attending/Staff)</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    {role === 'Other' && <input type="text" placeholder="Please specify role" value={roleOther} onChange={e => setRoleOther(e.target.value)} className="w-full bg-gray-100 text-gray-800 p-3 rounded-md border border-gray-300 placeholder-gray-400" />}
                                    <select value={practiceField} onChange={e => setPracticeField(e.target.value)} className="w-full bg-gray-100 text-gray-800 p-3 rounded-md border border-gray-300">
                                        <option value="">Select Field of Practice...</option>
                                        <option>Paediatric Nephrology</option>
                                        <option>Adult Nephrology</option>
                                        <option>Pathology</option>
                                        <option>Paediatric Rheumatology</option>
                                        <option>Adult Rheumatology</option>
                                        <option>General Paediatrics</option>
                                        <option>Internal Medicine</option>
                                        <option>Family Medicine</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    {practiceField === 'Other' && <input type="text" placeholder="Please specify field" value={practiceFieldOther} onChange={e => setPracticeFieldOther(e.target.value)} className="w-full bg-gray-100 text-gray-800 p-3 rounded-md border border-gray-300 placeholder-gray-400" />}
                                    <input type="text" placeholder="Country" value={country} onChange={e => setCountry(e.target.value)} className="w-full bg-gray-100 text-gray-800 p-3 rounded-md border border-gray-300 placeholder-gray-400" />
                                    <textarea value={comments} onChange={e => setComments(e.target.value)} placeholder="Any other comments or suggestions? (Optional)" className="w-full bg-gray-100 text-gray-800 p-3 rounded-md border border-gray-300 placeholder-gray-400" rows="3"></textarea>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-4 p-6 border-t">
                            <button onClick={onClose} className="text-gray-600 font-semibold py-2 px-4 rounded-lg hover:bg-gray-100">Cancel</button>
                            <button onClick={handleSubmit} disabled={isSubmitting} className="bg-[#4F2683] text-white font-semibold py-2 px-6 rounded-lg shadow-md hover:bg-opacity-90 disabled:bg-gray-400">
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </>
                )}
            </motion.div>
        </motion.div>
    );
};

const ImageModal = ({ imageUrls, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev + 1) % imageUrls.length);
    };

    const handlePrev = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length);
    };

    return (
        <motion.div 
            className="fixed inset-0 bg-black bg-opacity-75 z-[100] flex justify-center items-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
        >
            <motion.div 
                className="relative bg-white p-2 rounded-lg shadow-2xl max-w-4xl max-h-[90vh] flex flex-col justify-center items-center"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                onClick={(e) => e.stopPropagation()}
            >
                <img src={imageUrls[currentIndex]} alt="Histology Slide" className="max-w-full max-h-[85vh] object-contain rounded"/>
                <button onClick={onClose} className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow-lg hover:bg-gray-200 z-10">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
                {imageUrls.length > 1 && (
                    <>
                        <button onClick={handlePrev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-50 rounded-full p-2 hover:bg-opacity-75">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                        </button>
                        <button onClick={handleNext} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-50 rounded-full p-2 hover:bg-opacity-75">
                           <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                        </button>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white text-xs font-semibold px-2 py-1 rounded-full">
                            {currentIndex + 1} / {imageUrls.length}
                        </div>
                    </>
                )}
            </motion.div>
        </motion.div>
    );
};



// --- MAIN APP COMPONENT ---
export default function App() {
    const [selectedPathologyKey, setSelectedPathologyKey] = useState('normal');
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);
    const [viewMode, setViewMode] = useState('histology');
    const [viewingImageUrls, setViewingImageUrls] = useState(null);
    const selectedPathology = interactionData[selectedPathologyKey];

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, user => {
            if (!user) {
                signInAnonymously(auth).catch(error => {
                    console.error("Anonymous sign-in failed:", error);
                });
            }
        });
        return () => unsubscribe();
    }, []);

    const handleSelection = (key) => {
        setSelectedPathologyKey(key);
        setDrawerOpen(false);
        logUsage(key, interactionData[key].name);
        
        logEvent(analytics, 'select_pathology', {
            pathology_key: key,
            pathology_name: interactionData[key].name
        });
    };

    const logUsage = async (buttonId, buttonName) => {
        if (!auth.currentUser) return;
        try {
            const sessionRef = doc(db, "sessions", auth.currentUser.uid);
            const clickData = {
                [`clicks.${buttonId}`]: increment(1),
                lastActivity: new Date(),
                sessionId: auth.currentUser.uid,
            };
            await setDoc(sessionRef, clickData, { merge: true });
        } catch (error) {
            console.error("Error logging usage: ", error);
        }
    };

    const handleFeedbackSubmit = async (feedbackData) => {
        try {
            await addDoc(collection(db, "feedback"), feedbackData);
            console.log("Feedback submitted successfully");
        } catch (error) {
            console.error("Error submitting feedback: ", error);
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans text-gray-800">
            <div className="container mx-auto p-4 md:p-8">
                <header className="relative text-center mb-4">
                    <div className="flex justify-center items-center gap-4">
                         <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-400"><path d="M12 20a8 8 0 0 0 8-8c0-2.5-1.14-4.7-2.9-6.14"/><path d="M12 20a8 8 0 0 1-8-8c0-2.5 1.14-4.7 2.9-6.14"/><path d="M12 4v16"/><path d="M12 4c2.5 0 4.7 1.14 6.14 2.9"/><path d="M12 4c-2.5 0-4.7 1.14-6.14 2.9"/><path d="M8.86 11.14 12 8l3.14 3.14"/><path d="M12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z"/></svg>
                        <h1 className="text-4xl md:text-5xl font-bold text-red-600">The Interactive Glomerulus</h1>
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400"><path d="m21 21-3.4-3.4"/><circle cx="10" cy="10" r="8"/><path d="M10 2a8 8 0 0 0-8 8"/><path d="M2 10a8 8 0 0 0 8 8"/></svg>
                    </div>
                    <p className="mt-3 text-lg" style={{color: '#4F2683'}}>A Visual Exploration of Glomerular Pathology</p>
                    <div className="mt-4 lg:absolute lg:top-0 lg:right-0 lg:mt-0">
                        <button onClick={() => setFeedbackModalOpen(true)} className="text-sm font-semibold text-white bg-green-500 rounded-full px-4 py-2 hover:bg-green-600 transition-colors shadow-md">
                            Give Feedback
                        </button>
                    </div>
                </header>
                
                <div className="flex justify-center my-6">
                    <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
                </div>


                <div className="lg:grid lg:grid-cols-5 lg:gap-8">
                    {/* Desktop Sidebar */}
                    <div className="hidden lg:block lg:col-span-1">
                         <div className="sticky top-8">
                            <ControlsPanel selectedKey={selectedPathologyKey} onPathologyChange={handleSelection} />
                         </div>
                    </div>

                    {/* Main Content */}
                    <main className="lg:col-span-4">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={viewMode}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.5 }}
                            >
                                {viewMode === 'histology' ? (
                                    <div className="flex flex-col gap-8">
                                        <GlomerulusVisualization pathology={selectedPathology} onViewSlide={setViewingImageUrls} />
                                        <FiltrationBarrierVisualization pathology={selectedPathology} onViewSlide={setViewingImageUrls} />
                                    </div>
                                ) : (
                                    <FunctionalOutcomeVisualization pathology={selectedPathology} />
                                )}
                            </motion.div>
                        </AnimatePresence>
                        <InfoPanel pathology={selectedPathology} />
                    </main>
                </div>
            </div>

            {/* Mobile Drawer Button */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white to-transparent z-40">
                <motion.button 
                    onClick={() => setDrawerOpen(true)}
                    className="w-full bg-[#4F2683] text-white font-bold py-4 rounded-xl shadow-lg"
                    whileTap={{ scale: 0.95 }}
                >
                    Select Pathology
                </motion.button>
            </div>

            {/* Mobile Drawer */}
            <AnimatePresence>
                {isDrawerOpen && (
                    <motion.div 
                        className="lg:hidden fixed inset-0 z-50 flex flex-col"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <div onClick={() => setDrawerOpen(false)} className="absolute inset-0 bg-black bg-opacity-50"></div>
                        <motion.div 
                            className="relative mt-auto bg-white rounded-t-2xl p-4 h-[80vh] flex flex-col"
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        >
                            <div className="flex-shrink-0 flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold">Select Pathology</h2>
                                <button onClick={() => setDrawerOpen(false)} className="p-2 rounded-full hover:bg-gray-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                </button>
                            </div>
                            <div className="flex-grow overflow-y-auto">
                                <ControlsPanel selectedKey={selectedPathologyKey} onPathologyChange={handleSelection} />
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            
            <AnimatePresence>
                {isFeedbackModalOpen && <FeedbackModal onClose={() => setFeedbackModalOpen(false)} onSubmit={handleFeedbackSubmit} />}
            </AnimatePresence>
            
            <AnimatePresence>
                {viewingImageUrls && <ImageModal imageUrls={viewingImageUrls} onClose={() => setViewingImageUrls(null)} />}
            </AnimatePresence>

            <footer className="text-center mt-12 pb-24 lg:pb-0 text-gray-500 text-sm">
                <p>Created by Dr. Amrit Kirpalani MD, MEHP, FRCPC. Histology slides curated by Dr. Katherina Baranova MD. For educational purposes only. Not intended for clinical decision-making.</p>
            </footer>
        </div>
    );
}